import {
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { documentDownloadXlsxAsync } from "../../lib/downloadFileXslx";
import { useParams } from "react-router-dom";
import {
  companyFollowUpAsync,
  selectCompanyFollowUp,
  selectCompanyFollowUpStatus,
} from "./followUpSlice";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  type CompanyFollowUp,
  type CompanyGroupRowDisplayed,
} from "./followUpApi";
import styled from "styled-components";

export const CompanyGroupFollowUpTab = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { t } = useTranslation();

  const companyFollowUpList = useAppSelector(selectCompanyFollowUp);
  const companyFollowUpStatus = useAppSelector(selectCompanyFollowUpStatus);
  const [listDisplayed, setListDisplayed] = useState<
    CompanyGroupRowDisplayed[] | null
  >(null);

  const pageName = "companyGroupFollowUp";
  const apiRef = useGridApiRef();
  const key = "company-group-grid";

  const aggregation = {
    model: {
      activeCardsCount: "sum",
      totalCardsCount: "sum",
      inOppositionCardsCount: "sum",
      activeVehiclesCount: "sum",
      activeConnectedVehiclesCount: "sum",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const rows: (CompanyGroupRowDisplayed & { id: string })[] = useMemo(() => {
    const companiesWithGroup = companyFollowUpList.filter(
      (c) => c.companyGroup !== null,
    );

    const oldestByGroup = new Map<string, CompanyFollowUp>();
    for (const company of companiesWithGroup) {
      const groupKey = company.companyGroup as string;
      const current = oldestByGroup.get(groupKey);
      if (!current || new Date(company.createdAt) < new Date(current.createdAt))
        oldestByGroup.set(groupKey, company);
    }

    return companiesWithGroup.map((company) => {
      const groupKey = company.companyGroup as string;
      const companyGroupName = `${groupKey} - ${oldestByGroup.get(groupKey)?.name ?? ""}`;
      return {
        id: company.companyUuid,
        companyGroupName,
        name: company.name,
        activeCardsCount: company.activeCardsCount,
        totalCardsCount: company.totalCardsCount,
        inOppositionCardsCount: company.inOppositionCardsCount,
        activeVehiclesCount: company.activeVehiclesCount,
        activeConnectedVehiclesCount: company.activeConnectedVehiclesCount,
      };
    });
  }, [companyFollowUpList]);

  const columnsDisplayed: {
    [index in keyof CompanyGroupRowDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      activeCardsCount: {
        type: "number",
        width: 140,
        valueFormatter: (value: number) => value ?? 0,
      },
      totalCardsCount: {
        type: "number",
        width: 170,
        valueFormatter: (value: number) => value ?? 0,
      },
      inOppositionCardsCount: {
        type: "number",
        width: 160,
        valueFormatter: (value: number) => value ?? 0,
      },
      activeVehiclesCount: {
        type: "number",
        width: 160,
        valueFormatter: (value: number) => value ?? 0,
      },
      activeConnectedVehiclesCount: {
        type: "number",
        width: 170,
        valueFormatter: (value: number) => value ?? 0,
      },
      name: {
        type: "string",
        width: 250,
      },
      companyGroupName: {
        type: "string",
        width: 600,
      },
    };
  }, []);

  const searchBarProps = {
    values: rows,
    setFilterValues: setListDisplayed,
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName:
            "QIVIA_" + t("companiesData.companyGroupFollowUp") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  useEffect(() => {
    if (companyFollowUpStatus === "idle") {
      void dispatch(companyFollowUpAsync());
    }
  }, [companyFollowUpStatus, dispatch]);

  useEffect(() => {
    if (rows && companyFollowUpStatus === "success") {
      setListDisplayed(rows);
    }
  }, [rows, companyFollowUpStatus]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "companyGroupFollowUp") return;

  return (
    <StyledContainer>
      <Spacer y={1} />
      <MaterialUiTable<keyof CompanyGroupRowDisplayed, CompanyGroupRowDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        groupingColumn={["companyGroupName"]}
        groupingFirstColumnDisplayed={"name"}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  .MuiDataGrid-container--bottom {
    display: none;
  }
`;
